import { BottomDrawerModalAnimated } from "@/components/shared/bottom-drawer-modal-animated";
import { Button, View } from "@/components/ui";
import { SectionSelectServices } from "./section-select-services";
import { SectionSelectDuration } from "./section-select-duration";
import { useBookingState } from "@/store/booking/useBookingState";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import { useEffect, useMemo, useState } from "react";
import {
  HomeCleaningFrequencyOptions,
  ServiceDurationOptions,
} from "@/constants/booking";
import type {
  DurationOption,
  FrequencyOption,
} from "@/components/shared/service-radio-item/types";
import { PackageFrequencyGroupEnum } from "@/types/booking";
import {
  getBookingPackages,
  getDateRangeFromFrequency,
} from "@/lib/booking-lib";

const RECURRING_FREQUENCY_OPTIONS = HomeCleaningFrequencyOptions[0];

const DEFAULT_RECURRING_OPTION = HomeCleaningFrequencyOptions[0][0]; // WEEKLY

export function SwitchToRecurringDrawer({
  open,
  onOpenChange,
  onOpenFindHelper,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenFindHelper(): void;
}) {
  const {
    data: { department, packageDetailsFilter },
    setBookingState,
  } = useBookingState();
  const { fetch: getAllClientPackage, loading } =
    useAllClientPackageDetailsStore();

  const [localFrequency, setLocalFrequency] = useState<FrequencyOption>(
    DEFAULT_RECURRING_OPTION,
  );
  const [localDuration, setLocalDuration] = useState<number>(
    packageDetailsFilter.duration ?? 4,
  );

  // Reset local selections each time the drawer opens
  useEffect(() => {
    if (open) {
      setLocalFrequency(
        HomeCleaningFrequencyOptions[0].find(
          (opt) => opt.frequency === packageDetailsFilter.frontEndFrequency,
        ) ?? DEFAULT_RECURRING_OPTION,
      );
      setLocalDuration(packageDetailsFilter.duration ?? 4);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, [open]);

  const selectedFrequencyOption = localFrequency;

  const durationSubTitles = useMemo(() => {
    switch (selectedFrequencyOption.frequency) {
      case PackageFrequencyGroupEnum.WEEKLY:
      case PackageFrequencyGroupEnum.FORTNIGHT:
        return ["$18/hr - $20/hr", "$18/hr - $22/hr"];
      default:
        return ["", ""];
    }
  }, [selectedFrequencyOption.frequency]);

  const onChooseFrequency = (item: FrequencyOption) => {
    if (item.openModal) {
      onOpenFindHelper();
      return;
    }
    setLocalFrequency(item);
  };

  const onSelectDuration = (option: DurationOption) => {
    setLocalDuration(option.duration);
  };

  const onProceed = () => {
    const {
      frontEndFrequency: _frontEndFrequency,
      duration: _duration,
      repeatEvery: _repeatEvery,
      repeatEveryTimes: _repeatEveryTimes,
      ...filters
    } = packageDetailsFilter;
    const [startDate, endDate] = getDateRangeFromFrequency(
      localFrequency.frequency,
    );
    getAllClientPackage({
      requestPayload: {
        filters: {
          ...filters,
          repeatEvery: [localFrequency.repeatEvery],
          repeatEveryTimes: localFrequency.repeatEveryTimes,
          duration: localDuration,
          department,
          active: true,
        },
      },
    }).then((res) => {
      if (res.error || !res.data) return;
      const packages = getBookingPackages(department, res.data);
      setBookingState({
        startDate,
        endDate,
        selectedSlot: null,
        packageDetailsFilter: {
          ...packageDetailsFilter,
          repeatEvery: [localFrequency.repeatEvery],
          repeatEveryTimes: localFrequency.repeatEveryTimes,
          frontEndFrequency: localFrequency.frequency,
          duration: localDuration,
        },
        selectedPackageIds: packages.map((pkg) => pkg.id),
      });
      onOpenChange(false);
    });
  };

  return (
    <BottomDrawerModalAnimated
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <Button
          variant="primary"
          color="CTA"
          size="md"
          fullWidth="full"
          loading={loading}
          onClick={onProceed}
          iconName="arrowRight"
          iconAlignment="end"
        >
          Switch to Recurring
        </Button>
      }
    >
      <View className="flex flex-col gap-2">
        <SectionSelectServices
          selectedFrequencyOption={selectedFrequencyOption}
          frequencyOptions={RECURRING_FREQUENCY_OPTIONS}
          onChooseFrequency={onChooseFrequency}
        />
        <SectionSelectDuration
          selectedDuration={localDuration}
          durationOptions={ServiceDurationOptions}
          durationSubTitles={durationSubTitles}
          onSelectDuration={onSelectDuration}
        />
      </View>
    </BottomDrawerModalAnimated>
  );
}
